import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, GaugeCircle, Fuel, ShieldCheck, ArrowRight, ArrowLeft,
  Baby, Radar, MapPin, Check, CalendarDays,
} from "lucide-react";
import { api, mediaUrl, formatApiError, effectivePricePerDay } from "../lib/api";

const EXTRAS = [
  { id: "detska_sedacka", icon: Baby, name: "Dětská sedačka", price: 200 },
  { id: "antiradar", icon: Radar, name: "Antiradar", price: 460 },
  { id: "pristaveni", icon: MapPin, name: "Přistavení vozu k zákazníkovi", price: 0, note: "cena dle km" },
];

const today = () => new Date().toISOString().split("T")[0];
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x.toISOString().split("T")[0];
};

export default function Rezervace() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [carId, setCarId] = useState(params.get("car") || "");
  const [picking, setPicking] = useState(false);

  const [pickupDate, setPickupDate] = useState(today());
  const [pickupTime, setPickupTime] = useState("10:00");
  const [returnDate, setReturnDate] = useState(addDays(today(), 3));
  const [returnTime, setReturnTime] = useState("10:00");

  const [form, setForm] = useState({ customer_name: "", phone: "", email: "", address: "", note: "" });
  const [extras, setExtras] = useState([]);
  const [payment, setPayment] = useState("pobocka");
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [deliveryAddr, setDeliveryAddr] = useState("");
  const [deliveryCity, setDeliveryCity] = useState("");
  const [deliveryQuote, setDeliveryQuote] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);

  useEffect(() => {
    api.get("/cars").then((r) => {
      setCars(r.data);
      if (!carId && r.data.length) setCarId(r.data[0].id);
    }).catch(() => {});
  }, []); // eslint-disable-line

  const car = useMemo(() => cars.find((c) => c.id === carId), [cars, carId]);

  const [booked, setBooked] = useState([]);
  useEffect(() => {
    if (!carId) return;
    api.get(`/cars/${carId}/booked`).then((r) => setBooked(r.data)).catch(() => setBooked([]));
  }, [carId]);

  const overlaps = (aS, aE, bS, bE) => aS < bE && bS < aE;
  const conflict = useMemo(
    () => booked.some((b) => overlaps(pickupDate, returnDate, b.pickup_date, b.return_date)),
    [booked, pickupDate, returnDate]
  );
  const fmtCz = (d) => (d ? d.split("-").reverse().join(". ") : "");

  const days = useMemo(() => {
    const d1 = new Date(pickupDate);
    const d2 = new Date(returnDate);
    const diff = Math.ceil((d2 - d1) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 1;
  }, [pickupDate, returnDate]);

  const extrasTotal = useMemo(
    () => extras.reduce((s, id) => s + (EXTRAS.find((e) => e.id === id)?.price || 0), 0) * days,
    [extras, days]
  );
  const perDay = useMemo(() => effectivePricePerDay(car, days), [car, days]);
  const carTotal = perDay * days;
  const baseTotal = (car?.price_per_day || 0) * days;
  const saving = baseTotal - carTotal;
  const deliveryActive = extras.includes("pristaveni");
  const deliveryPrice = deliveryActive && deliveryQuote ? deliveryQuote.price : 0;
  const total = carTotal + extrasTotal + deliveryPrice;

  const sortedTiers = useMemo(
    () => [...(car?.price_tiers || [])].filter((t) => t.min_days && t.price_per_day).sort((a, b) => a.min_days - b.min_days),
    [car]
  );

  const toggleExtra = (id) =>
    setExtras((v) => {
      const next = v.includes(id) ? v.filter((x) => x !== id) : [...v, id];
      if (id === "pristaveni" && !next.includes("pristaveni")) setDeliveryQuote(null);
      return next;
    });

  const calcDelivery = async () => {
    if (!deliveryAddr.trim() || !deliveryCity.trim()) return toast.error("Zadejte adresu i město.");
    setCalcLoading(true);
    try {
      const { data } = await api.post("/delivery/quote", { address: deliveryAddr, city: deliveryCity });
      setDeliveryQuote(data);
      toast.success(`Vzdálenost ${data.distance_km} km — přistavení ${data.price.toLocaleString("cs-CZ")} Kč`);
    } catch (err) {
      setDeliveryQuote(null);
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCalcLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!car) return toast.error("Vyberte prosím vůz.");
    if (!form.customer_name || !form.phone || !form.email)
      return toast.error("Vyplňte prosím jméno, telefon a e-mail.");
    if (!agree) return toast.error("Potvrďte prosím souhlas s podmínkami.");
    if (conflict) return toast.error("Vůz je ve zvoleném termínu obsazený. Vyberte prosím jiný termín.");
    if (deliveryActive && !deliveryQuote) return toast.error("Spočítejte prosím vzdálenost přistavení pomocí tlačítka Spočítat.");

    setSubmitting(true);
    try {
      await api.post("/reservations", {
        car_id: car.id,
        car_name: car.name,
        pickup_date: pickupDate,
        pickup_time: pickupTime,
        return_date: returnDate,
        return_time: returnTime,
        ...form,
        extras,
        payment_method: payment,
        total_price: total,
        days,
        delivery_address: deliveryActive ? deliveryAddr : "",
        delivery_city: deliveryActive ? deliveryCity : "",
        delivery_distance_km: deliveryActive && deliveryQuote ? deliveryQuote.distance_km : 0,
        delivery_price: deliveryPrice,
      });
      toast.success("Rezervace odeslána! Brzy se vám ozveme s potvrzením.");
      navigate("/rezervace/hotovo");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const times = Array.from({ length: 15 }, (_, i) => `${String(7 + i).padStart(2, "0")}:00`);
  const inputCls =
    "w-full bg-[#151515] border border-[#1f1f1f] text-white rounded-sm px-4 py-3 text-sm focus:border-[#B58A50] outline-none";

  return (
    <div className="bg-black min-h-screen" data-testid="rezervace-page">
      <div className="container-kame pt-8 pb-3">
        <p className="text-white/40 text-sm">
          <Link to="/" className="hover:text-[#B58A50]">Úvod</Link> <span className="mx-1">›</span> Rezervace
        </p>
      </div>

      <section className="container-kame pb-6">
        <p className="eyebrow">Rezervace vozu</p>
        <h1 className="font-display text-4xl sm:text-5xl text-white uppercase mt-2">Rezervujte si vůz</h1>
        <p className="text-white/60 mt-3">Vyplňte formulář a my se vám co nejdříve ozveme s potvrzením rezervace.</p>
        <div className="w-16 h-1 bg-[#B58A50] mt-5" />
      </section>

      <form onSubmit={submit} className="container-kame pb-20 grid lg:grid-cols-[1fr_380px] gap-8 items-start">
        {/* LEFT */}
        <div className="space-y-8">
          {/* step 1 */}
          <div>
            <h2 className="font-display text-xl text-white uppercase mb-4">1. Vyberte vůz a termín</h2>
            <div className="card-kame p-4 flex items-center gap-4">
              <img src={mediaUrl(car?.image_url)} alt={car?.name} className="w-24 h-16 object-cover rounded-sm bg-[#0a0a0a]" />
              <div className="flex-1">
                <p className="font-display text-lg text-white uppercase">{car?.name || "—"}</p>
                <p className="text-white/50 text-sm">{[car?.engine, car?.transmission, car?.fuel].filter(Boolean).join(" | ")}</p>
              </div>
              <button
                type="button"
                onClick={() => setPicking((v) => !v)}
                className="btn-outline-gold text-xs py-2 px-4"
                data-testid="change-car-btn"
              >
                Změnit vůz
              </button>
            </div>

            {picking && (
              <div className="card-kame mt-3 p-3 max-h-72 overflow-y-auto grid sm:grid-cols-2 gap-2" data-testid="car-picker">
                {cars.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => { setCarId(c.id); setPicking(false); }}
                    className={`flex items-center gap-3 p-2 rounded-sm text-left transition-colors ${
                      c.id === carId ? "bg-[#B58A50]/15 border border-[#B58A50]" : "hover:bg-[#151515] border border-transparent"
                    }`}
                    data-testid={`pick-car-${c.id}`}
                  >
                    <img src={mediaUrl(c.image_url)} alt={c.name} className="w-14 h-10 object-cover rounded-sm bg-[#0a0a0a]" />
                    <div>
                      <p className="text-white text-sm">{c.name}</p>
                      <p className="text-[#B58A50] text-xs">{c.price_per_day.toLocaleString("cs-CZ")} Kč / den</p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider">Datum a čas vyzvednutí</label>
                <div className="flex gap-2 mt-2">
                  <input type="date" min={today()} value={pickupDate} onChange={(e) => setPickupDate(e.target.value)} className={inputCls} data-testid="pickup-date" />
                  <select value={pickupTime} onChange={(e) => setPickupTime(e.target.value)} className={inputCls + " w-28"} data-testid="pickup-time">
                    {times.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider">Datum a čas vrácení</label>
                <div className="flex gap-2 mt-2">
                  <input type="date" min={pickupDate} value={returnDate} onChange={(e) => setReturnDate(e.target.value)} className={inputCls} data-testid="return-date" />
                  <select value={returnTime} onChange={(e) => setReturnTime(e.target.value)} className={inputCls + " w-28"} data-testid="return-time">
                    {times.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <p className="text-white/40 text-xs mt-2 flex items-center gap-2">
              <CalendarDays size={14} className="text-[#B58A50]" /> Doba pronájmu: {days} {days === 1 ? "den" : days < 5 ? "dny" : "dní"}
            </p>

            <div
              className={`mt-3 rounded-sm p-3 text-sm border ${
                conflict ? "border-red-500/50 bg-red-500/10 text-red-300" : "border-green-500/40 bg-green-500/10 text-green-300"
              }`}
              data-testid="availability-status"
            >
              {conflict
                ? "Ve zvoleném termínu je vůz již obsazený. Zvolte prosím jiný termín."
                : "Skvělé! Vůz je ve zvoleném termínu volný."}
            </div>

            {booked.length > 0 && (
              <div className="mt-3 card-kame p-3" data-testid="booked-ranges">
                <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Obsazené termíny tohoto vozu</p>
                <div className="flex flex-wrap gap-2">
                  {booked.map((b, i) => {
                    let label;
                    if (b.return_date >= "9999") label = `od ${fmtCz(b.pickup_date)} (trvale)`;
                    else if (b.pickup_date <= "1900-01-01") label = `do ${fmtCz(b.return_date)}`;
                    else label = `${fmtCz(b.pickup_date)} – ${fmtCz(b.return_date)}`;
                    return (
                      <span key={i} className="text-xs bg-[#151515] border border-[#1f1f1f] text-white/70 px-2.5 py-1 rounded-sm">
                        {label}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {sortedTiers.length > 0 && (
            <div className="card-kame p-4" data-testid="tier-info">
              <p className="eyebrow mb-3">Zvýhodněné ceny za delší pronájem</p>
              <div className="flex flex-wrap gap-2">
                <span className={`text-xs px-3 py-1.5 rounded-sm border ${days < (sortedTiers[0]?.min_days || 99999) ? "border-[#B58A50] bg-[#B58A50]/10 text-[#B58A50]" : "border-[#1f1f1f] bg-[#151515] text-white/60"}`}>
                  1+ dní: {(car?.price_per_day || 0).toLocaleString("cs-CZ")} Kč/den
                </span>
                {sortedTiers.map((t, i) => {
                  const next = sortedTiers[i + 1];
                  const active = days >= t.min_days && (!next || days < next.min_days);
                  return (
                    <span key={i} className={`text-xs px-3 py-1.5 rounded-sm border ${active ? "border-[#B58A50] bg-[#B58A50]/10 text-[#B58A50]" : "border-[#1f1f1f] bg-[#151515] text-white/60"}`}>
                      od {t.min_days} dní: {t.price_per_day.toLocaleString("cs-CZ")} Kč/den
                    </span>
                  );
                })}
              </div>
              <p className="text-white/40 text-xs mt-3">Sleva se uplatní automaticky podle délky pronájmu.</p>
            </div>
          )}

          {/* step 2 */}
          <div>
            <h2 className="font-display text-xl text-white uppercase mb-4">2. Vaše údaje</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider">Jméno a příjmení</label>
                <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Zadejte vaše jméno" className={inputCls + " mt-2"} data-testid="res-name" />
              </div>
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider">Telefon</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Zadejte váš telefon" className={inputCls + " mt-2"} data-testid="res-phone" />
              </div>
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider">E-mail</label>
                <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Zadejte váš e-mail" className={inputCls + " mt-2"} data-testid="res-email" />
              </div>
              <div>
                <label className="text-white/50 text-xs uppercase tracking-wider">Adresa</label>
                <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Zadejte adresu" className={inputCls + " mt-2"} data-testid="res-address" />
              </div>
            </div>
            <div className="mt-4">
              <label className="text-white/50 text-xs uppercase tracking-wider">Poznámka (volitelné)</label>
              <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={3} placeholder="Napište nám případnou poznámku" className={inputCls + " mt-2 resize-none"} data-testid="res-note" />
            </div>
          </div>

          {/* step 3 */}
          <div>
            <h2 className="font-display text-xl text-white uppercase mb-4">3. Doplňkové služby <span className="text-white/40 text-sm normal-case">(volitelné)</span></h2>
            <div className="grid sm:grid-cols-3 gap-3">
              {EXTRAS.map((ex) => {
                const active = extras.includes(ex.id);
                return (
                  <button
                    type="button"
                    key={ex.id}
                    onClick={() => toggleExtra(ex.id)}
                    className={`card-kame p-4 text-left flex items-start gap-3 transition-colors ${active ? "border-[#B58A50] bg-[#B58A50]/10" : "hover:border-[#B58A50]/50"}`}
                    data-testid={`extra-${ex.id}`}
                  >
                    <span className={`w-5 h-5 rounded-sm border flex items-center justify-center shrink-0 mt-0.5 ${active ? "bg-[#B58A50] border-[#B58A50]" : "border-white/30"}`}>
                      {active && <Check size={14} className="text-black" />}
                    </span>
                    <span>
                      <ex.icon size={18} className="text-[#B58A50] mb-1" />
                      <span className="block text-white text-sm">{ex.name}</span>
                      <span className="block text-white/50 text-xs">{ex.price ? `${ex.price} Kč / den` : ex.note}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            {deliveryActive && (
              <div className="card-kame mt-3 p-4 space-y-3" data-testid="delivery-panel">
                <p className="text-white text-sm flex items-center gap-2"><MapPin size={16} className="text-[#B58A50]" /> Kam přistavit vůz?</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input value={deliveryAddr} onChange={(e) => { setDeliveryAddr(e.target.value); setDeliveryQuote(null); }} placeholder="Ulice a číslo popisné" className={inputCls} data-testid="delivery-address" />
                  <input value={deliveryCity} onChange={(e) => { setDeliveryCity(e.target.value); setDeliveryQuote(null); }} placeholder="Město" className={inputCls} data-testid="delivery-city" />
                </div>
                <button type="button" onClick={calcDelivery} disabled={calcLoading} className="btn-outline-gold text-sm disabled:opacity-60" data-testid="delivery-calc">
                  {calcLoading ? "Počítám…" : "Spočítat vzdálenost a cenu"}
                </button>
                {deliveryQuote && (
                  <div className="bg-[#0a0a0a] border border-[#1c1c1c] rounded-sm p-3 text-sm" data-testid="delivery-result">
                    <div className="flex justify-between text-white/70"><span>Vzdálenost{deliveryQuote.round_trip ? " (tam i zpět)" : ""}</span><span className="text-white">{deliveryQuote.distance_km} km</span></div>
                    {deliveryQuote.free_km > 0 && (
                      <div className="flex justify-between text-white/70"><span>Zdarma</span><span className="text-white">{deliveryQuote.free_km} km</span></div>
                    )}
                    <div className="flex justify-between mt-1 pt-1 border-t border-[#1c1c1c]"><span className="text-[#B58A50]">Cena přistavení</span><span className="text-[#B58A50] font-semibold">{deliveryQuote.price.toLocaleString("cs-CZ")} Kč</span></div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div>
            <h2 className="font-display text-xl text-white uppercase mb-4">4. Způsob platby</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { id: "pobocka", title: "Platba při převzetí vozu", sub: "Hotově nebo kartou na pobočce" },
                { id: "online", title: "Platba na bankovní účet", sub: "Zaplaťte zálohu převodem na účet, zbytek při převzetí" },
              ].map((p) => (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => setPayment(p.id)}
                  className={`card-kame p-4 text-left flex items-start gap-3 transition-colors ${payment === p.id ? "border-[#B58A50] bg-[#B58A50]/10" : "hover:border-[#B58A50]/50"}`}
                  data-testid={`payment-${p.id}`}
                >
                  <span className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 mt-0.5 ${payment === p.id ? "border-[#B58A50]" : "border-white/30"}`}>
                    {payment === p.id && <span className="w-2.5 h-2.5 bg-[#B58A50] rounded-full" />}
                  </span>
                  <span>
                    <span className="block text-white text-sm">{p.title}</span>
                    <span className="block text-white/50 text-xs">{p.sub}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <label className="flex items-start gap-3 text-white/70 text-sm cursor-pointer">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="accent-[#B58A50] w-4 h-4 mt-0.5" data-testid="res-agree" />
            <span>Souhlasím s <Link to="/podminky" className="text-[#B58A50] underline">podmínkami pronájmu</Link> a <Link to="/podminky" className="text-[#B58A50] underline">ochranou osobních údajů</Link>.</span>
          </label>

          <div className="flex flex-wrap gap-4">
            <Link to="/vozovy-park" className="btn-outline-gold" data-testid="back-to-fleet">
              <ArrowLeft size={18} /> Zpět k výběru vozů
            </Link>
            <button type="submit" disabled={submitting || conflict} className="btn-gold disabled:opacity-60 disabled:cursor-not-allowed" data-testid="submit-reservation">
              {submitting ? "Odesílám…" : "Odeslat rezervaci"} <ArrowRight size={18} />
            </button>
          </div>
        </div>

        {/* SUMMARY */}
        <aside className="card-kame p-6 lg:sticky lg:top-24" data-testid="reservation-summary">
          <h3 className="font-display text-lg text-white uppercase mb-4">Shrnutí rezervace</h3>
          <img src={mediaUrl(car?.image_url)} alt={car?.name} className="w-full h-40 object-cover rounded-sm bg-[#0a0a0a]" />
          <p className="font-display text-xl text-white uppercase mt-4">{car?.name || "—"}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <span className="inline-flex items-center gap-1.5 text-xs text-white/70 bg-[#151515] px-2.5 py-1 rounded-sm"><Users size={13} className="text-[#B58A50]" /> {car?.seats} míst</span>
            <span className="inline-flex items-center gap-1.5 text-xs text-white/70 bg-[#151515] px-2.5 py-1 rounded-sm"><GaugeCircle size={13} className="text-[#B58A50]" /> {car?.transmission}</span>
            <span className="inline-flex items-center gap-1.5 text-xs text-white/70 bg-[#151515] px-2.5 py-1 rounded-sm"><Fuel size={13} className="text-[#B58A50]" /> {car?.fuel}</span>
          </div>

          <div className="mt-5 space-y-2 text-sm border-t border-[#1c1c1c] pt-4">
            <Row label="Vyzvednutí" value={`${pickupDate.split("-").reverse().join(". ")} v ${pickupTime}`} />
            <Row label="Vrácení" value={`${returnDate.split("-").reverse().join(". ")} v ${returnTime}`} />
            <Row label="Doba pronájmu" value={`${days} ${days === 1 ? "den" : days < 5 ? "dny" : "dní"}`} />
          </div>

          <div className="mt-4 space-y-2 text-sm border-t border-[#1c1c1c] pt-4">
            <div className="flex justify-between gap-2">
              <span className="text-white/50">Cena za den</span>
              <span className="text-white text-right">
                {saving > 0 && (
                  <span className="text-white/40 line-through mr-2">{(car?.price_per_day || 0).toLocaleString("cs-CZ")} Kč</span>
                )}
                <span className={saving > 0 ? "text-[#B58A50] font-semibold" : ""}>{perDay.toLocaleString("cs-CZ")} Kč</span>
              </span>
            </div>
            <Row label="Mezisoučet" value={`${carTotal.toLocaleString("cs-CZ")} Kč`} />
            {saving > 0 && (
              <div className="flex justify-between gap-2" data-testid="summary-saving">
                <span className="text-green-400">Sleva za dlouhodobý pronájem</span>
                <span className="text-green-400 text-right">− {saving.toLocaleString("cs-CZ")} Kč</span>
              </div>
            )}
            <Row label="Doplňkové služby" value={`${extrasTotal.toLocaleString("cs-CZ")} Kč`} />
            {deliveryPrice > 0 && (
              <Row label="Přistavení vozu" value={`${deliveryPrice.toLocaleString("cs-CZ")} Kč`} />
            )}
          </div>

          {saving > 0 && (
            <div className="mt-3 bg-green-500/10 border border-green-500/40 text-green-300 rounded-sm p-3 text-xs" data-testid="longterm-badge">
              Zvýhodněná denní sazba za pronájem na {days} dní.
            </div>
          )}

          <div className="mt-4 border-t border-[#1c1c1c] pt-4 flex items-end justify-between">
            <div>
              <p className="font-display text-lg text-white uppercase">Celkem</p>
              <p className="text-white/40 text-xs">bez DPH</p>
            </div>
            <p className="font-display text-3xl text-[#B58A50]" data-testid="summary-total">{total.toLocaleString("cs-CZ")} Kč</p>
          </div>

          <p className="text-white/45 text-xs mt-3" data-testid="summary-deposit">
            + vratná kauce {(car?.deposit ?? 10000).toLocaleString("cs-CZ")} Kč (vratíme po vrácení vozu bez poškození)
          </p>

          <div className="mt-5 bg-[#0a0a0a] border border-[#1c1c1c] rounded-sm p-4 flex items-start gap-3">
            <ShieldCheck size={20} className="text-[#B58A50] shrink-0 mt-0.5" />
            <div>
              <p className="text-white text-sm font-semibold uppercase">Bez starostí</p>
              <p className="text-white/50 text-xs">V ceně je pojištění a asistence. Nájezd 300–500 km/den.</p>
            </div>
          </div>
        </aside>
      </form>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-2">
    <span className="text-white/50">{label}</span>
    <span className="text-white text-right">{value}</span>
  </div>
);
